import { config } from '../config';
import { read, TABLE } from '../data/db';
import { hypotheses, matches, profile, teams } from '../data/repos';
import { deriveCarryForward, toHypothesisRow } from '../domain/setup';
import type { CarryCandidate, PreMatchCandidate } from '../domain/setup';
import { MATCH_STATE } from '../domain/machine';
import type {
  CapturePhase,
  Foot,
  MatchRow,
  PlayerRow,
  TeamRow,
  TemplateRow,
} from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';
import type { MatchSetupPatch } from '../domain/commands';
import { newId } from './ids';
import { log } from './logger';

/**
 * PRE-1 … PRE-7 — the **I/O half** of Pre-Match: reads for the screen, dispatches for its controls.
 *
 * §1.2 forbids `ui/` from importing `data/` at all, so every row the Pre-Match screen renders
 * arrives through this module and every write leaves through it. That is not ceremony — it is what
 * keeps the screen free of storage vocabulary, and it is where the transaction boundaries live.
 *
 * **The decisions are not here.** `domain/setup.ts` owns PRE-5's dedup and ordering, PRE-4's
 * substitution, §3.2's bucket mapping and the candidate → row construction; `domain/commands/setup`
 * owns every guard. This module reads, mints ids, and dispatches. A function here that computed a
 * bucket, a status or a position would be the second copy U-2's inheritance note warns about.
 */

/**
 * OBS-1's module category.
 *
 * **Not `'preMatch'`** — the `ses-1/match-state-literals` rule fired on that string here, exactly as
 * it fired on `SURFACE.preMatch` in U-4's `resume.ts`. That is the guard working: a log category and
 * a `MatchState` spelling the same word is how a hand-rolled state grouping begins. §2.2's own
 * vocabulary for this thing is "an unstarted setup", so the name was available.
 */
const MODULE = 'matchSetup';

// ─────────────────────────────────────────────────────────────────────────────
// Reads
// ─────────────────────────────────────────────────────────────────────────────

export interface SetupContext {
  /** FILE-1's opponent list, by name — PRE-1's "select opponent Team" half. */
  readonly teams: readonly TeamRow[];
  /** PRE-2's library, plus any PRE-6 user templates once U-16 lands. */
  readonly templates: readonly TemplateRow[];
  /** §2.2 — the unstarted setup this screen edits, if one exists. */
  readonly setup: MatchRow | undefined;
}

/**
 * Everything the screen needs before a Team is chosen, from **one** readonly transaction.
 *
 * One transaction because three independently-timed reads describe a moment that never existed: a
 * team created between the team read and the match read would render a setup whose opponent is not
 * in the picker.
 *
 * @throws {StorageError} on a read failure. Not swallowed — an empty screen and a failed read look
 *   identical to the user, and only one of them is recoverable by trying again.
 */
export async function loadSetupContext(): Promise<SetupContext> {
  return read(
    [TABLE.teams, TABLE.templates, TABLE.matches],
    async (): Promise<SetupContext> => {
      const [teamRows, templateRows, preMatchRows] = [
        await teams.listTeams(),
        await profile.listTemplates(),
        await matches.listMatchesByState([MATCH_STATE.preMatch]),
      ];
      return { teams: teamRows, templates: templateRows, setup: preMatchRows[0] };
    },
    'loadSetupContext',
  );
}

export interface TeamContext {
  /** PRE-5's candidates, already deduped, ordered and pre-checked. */
  readonly carryForward: readonly CarryCandidate[];
  /** FILE-2's roster, so a PRE-4 slot can bind to a shirt number this team already has on file. */
  readonly players: readonly PlayerRow[];
}

/**
 * PRE-5 — the carry-forward offer for a previously scouted Team.
 *
 * > *"Availability is keyed on hypothesis status from any prior match of that Team **regardless of
 * > that match's state**. It MUST NOT require the prior match to be `archived`, because
 * > distillation is asynchronous (SES-11) and a team may be scouted twice before either match is
 * > distilled."*
 *
 * `listMatchesForTeam` returns every match of the team in every state and **nothing here filters on
 * state**. The only exclusion is `excludeMatchId` — the setup being built, which is a match of this
 * team and would otherwise offer its own candidates back to itself the moment they were created.
 *
 * @throws {StorageError} on a read failure.
 */
export async function loadTeamContext(
  teamId: string,
  excludeMatchId: string | null,
): Promise<TeamContext> {
  return read(
    [TABLE.matches, TABLE.hypotheses, TABLE.players],
    async (): Promise<TeamContext> => {
      const priorMatches = (await matches.listMatchesForTeam(teamId)).filter(
        (row) => row.id !== excludeMatchId,
      );

      const priorHypotheses = [];
      for (const match of priorMatches) {
        priorHypotheses.push(...(await hypotheses.listHypothesesForMatch(match.id)));
      }

      return {
        carryForward: deriveCarryForward(priorHypotheses, priorMatches),
        players: await teams.listPlayersForTeam(teamId),
      };
    },
    'loadTeamContext',
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Writes — every one of them through `dispatch`
// ─────────────────────────────────────────────────────────────────────────────

/** A dispatch that also hands back the id it minted, so the screen can select what it just made. */
export type CreatedResult =
  | { readonly ok: true; readonly id: string }
  | { readonly ok: false; readonly code: string; readonly message: string };

function created(id: string, result: DispatchResult): CreatedResult {
  return result.ok ? { ok: true, id } : { ok: false, code: result.code, message: result.message };
}

/** PRE-1 — create the opponent. */
export async function createTeam(input: {
  name: string;
  club?: string | null;
  ageGroup?: string | null;
  notes?: string | null;
  now?: number;
}): Promise<CreatedResult> {
  const id = newId();
  return created(
    id,
    await dispatch({
      type: 'CreateTeam',
      row: {
        id,
        name: input.name.trim(),
        club: input.club ?? null,
        ageGroup: input.ageGroup ?? null,
        notes: input.notes ?? null,
        createdAt: input.now ?? Date.now(),
      },
    }),
  );
}

/**
 * §2.2 — `idle → preMatch`, with SES-5's default half length.
 *
 * Every derived column starts at its zero and the clock anchors start `null`: the four anchors are
 * SES-1's to write, and `capturePhase` starts `discovery` because PRE-7's "start locked" exit moves
 * it through `confirmLock` — the one writer INV-4 permits.
 *
 * A rejection with `ERR_SETUP_IN_PROGRESS` is **not** a failure state (§2.2): it means an unstarted
 * setup already exists, and the caller answers it by offering `DiscardSetup` at the point of
 * refusal. `ERR_MATCH_IN_PROGRESS` is SES-9's, and means a match is genuinely in an in-match state.
 *
 * ── `format` starts `null`, and is not a parameter ──────────────────────────
 *
 * PRE-8 (D-28): the format has **no default and no pre-selected value**; it *"MUST NOT be carried
 * forward from the Team's previous match, read from a global setting, or inferred from
 * `Team.ageGroup`."* This function fires the moment an opponent is chosen — before the user has been
 * asked anything — so `null` is the only honest value, and there is deliberately no input for a
 * caller to supply one. That is the whole of "no default": not a control that starts on 11v11, but a
 * column that starts empty and a command (`StartCapture`) that refuses until it is not.
 */
export async function createMatch(input: {
  teamId: string;
  date?: string;
  comp?: string | null;
  halfLengthMin?: number;
  formationGuess?: string | null;
}): Promise<CreatedResult> {
  const id = newId();
  return created(
    id,
    await dispatch({
      type: 'CreateMatch',
      row: {
        id,
        teamId: input.teamId,
        comp: input.comp ?? null,
        date: input.date ?? todayLocal(),
        // PRE-8 / D-28 — no default, no pre-selection, and nothing to read it from. See above.
        format: null,
        // B §4.1 (F-2, ruled) — same shape as `format` one line up: the purpose has no default
        // and the required-choice gate is B's setup UI, not the engine. A matches never set it
        // and A's UI never renders it, so `null` here changes nothing A does.
        purpose: null,
        /**
         * SES-5's default is now **derived from the format** (§19 F-41, ruled), and no format exists
         * yet — so this is a placeholder, not a default, and it is never shown to anyone.
         *
         * The column is non-nullable and `MatchSchema` requires a value inside SES-5's range, so
         * something has to be written here. What keeps it honest is the two facts around it: the
         * stepper renders **no number at all** while `format` is `null` (`TeamAndDetails.tsx`), and
         * `reconcileHalfLength` always replaces a placeholder when the format is chosen. Combined
         * with `StartCapture` refusing while `format` is null, no match can reach a capture surface
         * carrying this value unless 11v11 — whose default it is — was actually chosen.
         *
         * `HALF_LENGTH_DEFAULT` rather than an arbitrary legal number, so the one place SES-5's
         * literal 35 lives stays the one place it lives.
         */
        halfLengthMin: input.halfLengthMin ?? config.HALF_LENGTH_DEFAULT,
        formationGuess: input.formationGuess ?? null,
        kickoffTs: null,
        halfTs: null,
        kickoff2Ts: null,
        fullTimeTs: null,
        state: MATCH_STATE.preMatch,
        capturePhase: 'discovery',
        lockTs: null,
        abandoned: false,
        lastSyncedTs: null,
        lastSyncedHash: null,
        triagePassedTs: null,
      },
    }),
  );
}

/**
 * Today, as the `YYYY-MM-DD` §3.1 requires — in the **local** zone, not UTC.
 *
 * `toISOString().slice(0,10)` would be the obvious one line and would be wrong for exactly the
 * matches this app is for: an evening kickoff west of Greenwich is already tomorrow in UTC, so the
 * match would file itself under the wrong day in FILE-1's history and sort ahead of itself in
 * PRE-5's recency compare. A match date is a calendar fact about where the user is standing.
 */
export function todayLocal(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${String(now.getFullYear())}-${month}-${day}`;
}

/** PRE-1 / SES-5 — one setup control's value. */
export function updateSetup(matchId: string, patch: MatchSetupPatch): Promise<DispatchResult> {
  return dispatch({ type: 'UpdateMatchSetup', matchId, patch });
}

/**
 * §2.2 — `DiscardSetup`, the escape hatch that keeps `preMatch` from deadlocking the app.
 *
 * Reached two ways, both of which the screen offers: directly, from the Pre-Match screen; and at the
 * point of an `ERR_SETUP_IN_PROGRESS` refusal, as *"You have an unstarted setup for Riverside —
 * discard it?"*. It cascades per INV-8 and frees the `activeMatch` pointer, so this is not a
 * "delete" the user has to find — it is the offered route out.
 */
export function discardSetup(matchId: string): Promise<DispatchResult> {
  return dispatch({ type: 'Transition', event: 'DiscardSetup', matchId });
}

/**
 * PRE-4 / FILE-2 — resolves a shirt number to a `Player` id, creating the row on demand.
 *
 * Read first, then create only if absent. The read is a plain repo call rather than part of the
 * command because the caller needs the **id** in hand to build its `HypothesisRow`, and a snapshot
 * read inside the transaction cannot be handed back out of one.
 *
 * The `ERR_PLAYER_NUMBER_TAKEN` branch closes the window between the two: under ENV-10 there is one
 * writer and one single-flight queue, so it is very nearly unreachable — but "very nearly" is not a
 * reason to bind a hypothesis to an id that does not exist, so the handler refuses and this re-reads.
 *
 * @throws {StorageError} on a read failure.
 */
export async function resolvePlayer(
  teamId: string,
  number: number,
  foot: Foot | null = null,
): Promise<CreatedResult> {
  const existing = await teams.findPlayerByNumber(teamId, number);
  if (existing !== undefined) return { ok: true, id: existing.id };

  const id = newId();
  const result = await dispatch({
    type: 'CreatePlayer',
    row: { id, teamId, number, foot, notes: null },
  });

  if (!result.ok && result.code === 'ERR_PLAYER_NUMBER_TAKEN') {
    const raced = await teams.findPlayerByNumber(teamId, number);
    if (raced !== undefined) {
      log.warn(MODULE, 'a player row for that shirt number appeared between the read and the write', {
        teamId,
        number,
        id: raced.id,
      });
      return { ok: true, id: raced.id };
    }
  }

  return created(id, result);
}

/**
 * PRE-7 — the exit. Creates every selected candidate and, for "start locked", positions the chosen.
 *
 * The screen supplies candidates in **its own order** and `positionedIds` in **board order**;
 * `positionedIds` becomes `boardPosition` 0..k−1 through `planLockPositions` (§6.2's contiguous
 * tail-allocated prefix). Passing anything for `positionedIds` in `discovery` mode is rejected by
 * the handler rather than ignored — §6.0's "Start in Discovery assigns no `boardPosition`" is the
 * reason a later LOCK-4 confirm is never a second assignment (INV-4).
 *
 * ── `createdAt` is `now + index`, and the index is the load-bearing half ─────
 *
 * §3.2's `v002` column is DISC-4's third sort term, and §6.1 is explicit about what it buys:
 * *"before kickoff every row ties on both of the first two terms, so without it the entire
 * pre-kickoff board falls through to a random-UUID order."* Every candidate here is created in the
 * same instant, so one clock reading for all of them would leave them **tied at term 3** and the
 * board would still come up in `crypto.randomUUID` order — the column would be present and the
 * defect would be unchanged. That is the structure-versus-behaviour trap in miniature.
 *
 * The offset records a fact rather than inventing one: these rows *are* being created now, and the
 * user's selection order *is* known — it is the same order `defaultPositionedIds` and
 * `planLockPositions` already use to assign `boardPosition` (§6.2), so the pre-kickoff Discovery
 * order and the board's slot order agree instead of disagreeing at random. The fiction is bounded to
 * a few milliseconds and to a single batch.
 *
 * This is **not** the thing `v002`'s backfill refuses. That refusal is about rows whose creation
 * moment the database never held; nothing is guessed here.
 */
export function startCapture(
  matchId: string,
  candidates: readonly PreMatchCandidate[],
  mode: CapturePhase,
  positionedIds: readonly string[],
): Promise<DispatchResult> {
  const createdAt = Date.now();
  return dispatch({
    type: 'StartCapture',
    matchId,
    hypotheses: candidates.map((candidate, index) =>
      toHypothesisRow(matchId, candidate, createdAt + index),
    ),
    mode,
    positionedHypothesisIds: mode === 'locked' ? positionedIds : [],
  });
}
